// Copyright © 2026 Spicy Friends

import {
  BringTheHeat,
  FaqList,
  SplitFeatureSection,
  type SplitFeatureSectionPicture,
  type ValuePropItem,
  ValueProps,
} from '@repo/shared-client/components/blocks'
import { createFileRoute, Link, redirect } from '@tanstack/react-router'
import cliteraturePicture from '@web/src/assets/intro/intro-cliterature.webp?w=400;800;1200&format=avif;webp&as=picture'
import eventsPicture from '@web/src/assets/intro/intro-events.png?w=400;800;1200&format=avif;webp&as=picture'
import groupsPicture from '@web/src/assets/intro/intro-groups.png?w=400;800;1200&format=avif;webp&as=picture'
import heroPictureDesktop from '@web/src/assets/intro/intro-hero-desktop-1.webp?w=1024;1920&format=avif;webp&as=picture'
import heroPictureMobile from '@web/src/assets/intro/intro-hero-mobile-1.webp?w=400;640&format=avif;webp&as=picture'
import locationsPicture from '@web/src/assets/intro/intro-locations.png?w=400;800;1200&format=avif;webp&as=picture'
import missionPicture from '@web/src/assets/intro/intro-mission-bg.webp?w=640;1024;1920&format=avif;webp&as=picture'
import vendorMarketPicture from '@web/src/assets/intro/intro-vm-all.webp?w=400;800;1200&format=avif;webp&as=picture'
import { authQueryKeys } from '@web/src/hooks/use-auth'
import { useTranslation } from '@web/src/lib/intl/react'
import type { BetterAuthSessionData } from '@web/src/lib/yoga/yoga-auth-context'
import {
  BadgeCheckIcon,
  BookOpenIcon,
  CalendarIcon,
  CameraIcon,
  CompassIcon,
  HeartIcon,
  ImagesIcon,
  LibraryBigIcon,
  MapIcon,
  MessageCircleIcon,
  PartyPopperIcon,
  ShieldCheckIcon,
  SmartphoneIcon,
  StoreIcon,
  UsersIcon,
  WalletIcon,
} from 'lucide-react'
import { type ComponentProps } from 'react'
import { homePageHeadFromLoaderData } from './index.seo'

type IntroSplitFeatureSection = {
  eyebrow: string
  heading: string
  body: string
  imageSrc: string
  imageAlt: string
  imageSide: 'left' | 'right'
  linkLabel: string
  to: string
  /** CSS `object-position` for the cover crop (e.g. `center 20%` shows more of the top). */
  imageObjectPosition?: string
  imageProps?: ComponentProps<'img'>
  imagePicture?: SplitFeatureSectionPicture
}

function getFeatureCards(t: (text: string) => string) {
  return [
    { icon: <BookOpenIcon className="size-6" />, label: t('Cliterature'), to: '/publication/list' },
    { icon: <CalendarIcon className="size-6" />, label: t('Events'), to: '/event' },
    { icon: <CameraIcon className="size-6" />, label: t('Galleries'), to: '/auth/signup' },
    { icon: <MapIcon className="size-6" />, label: t('Groups'), to: '/group/list' },
    { icon: <CompassIcon className="size-6" />, label: t('Places'), to: '/place' },
    { icon: <MessageCircleIcon className="size-6" />, label: t('Private Chat'), to: '/auth/signup' },
    { icon: <UsersIcon className="size-6" />, label: t('Friends'), to: '/auth/signup' },
    { icon: <HeartIcon className="size-6" />, label: t('Sexy Posts'), to: '/auth/signup' },
    { icon: <StoreIcon className="size-6" />, label: t('Vendor Market'), to: '/vendor-market' },
  ] as const
}

function getValueProps(t: (text: string) => string): ValuePropItem[] {
  const iconClass = 'size-6 shrink-0 text-red-600'
  return [
    {
      icon: <WalletIcon className={iconClass} />,
      title: t('Free to Use'),
      text: t(
        'Create your profile, discover members, and connect with the community at no cost. Optional Supporter upgrades unlock advanced tools for power users.'
      ),
    },
    {
      icon: <BadgeCheckIcon className={iconClass} />,
      title: t('Member Verification'),
      text: t(
        'Profiles can be verified by trusted members. Verification status helps members quickly recognize established, active community participants.'
      ),
    },
    {
      icon: <CompassIcon className={iconClass} />,
      title: t('Smart Discovery'),
      text: t(
        'Browse events, places, and groups with live filters for tags, distance, and relevance so you can find the right vibe faster.'
      ),
    },
    {
      icon: <PartyPopperIcon className={iconClass} />,
      title: t('Events, Places & Groups'),
      text: t(
        'Plan nights out, discover lifestyle - friendly venues, and join niche groups that match your interests, location, and play style.'
      ),
    },
    {
      icon: <ImagesIcon className={iconClass} />,
      title: t('Gallery'),
      text: t('Enjoy sexy images and videos from our members, with the option to upload your own content.'),
    },
    {
      icon: <LibraryBigIcon className={iconClass} />,
      title: t('Cliterature'),
      text: t('Discover naughty literature that can get your imagination working on overdrive.'),
    },
    {
      icon: <SmartphoneIcon className={iconClass} />,
      title: t('Install on Your Phone'),
      text: t(
        'Spicy Friends is installable directly from this site as a mobile app (PWA) on iOS and Android for a home-screen, app-like experience.'
      ),
    },
    {
      icon: <ShieldCheckIcon className={iconClass} />,
      title: t('Data Privacy'),
      text: t(
        'Your privacy is important to us. We never share your data with third parties, and all information is deleted when your account is removed.'
      ),
    },
  ]
}

function getFaqItems(t: (text: string) => string) {
  return [
    {
      question: t('Is Spicy Friends free to use?'),
      answer: t(
        'Yes. Core membership is free, including profile setup and community discovery. We also offer optional Supporter upgrades for members who want extra capabilities like expanded limits and advanced creator tools.'
      ),
    },
    {
      question: t('What can I do on Spicy Friends?'),
      answer: t(
        'You can browse and filter events, places, and groups, connect with friends, chat privately, and share content. The platform is built for lifestyle discovery plus real community interaction.'
      ),
    },
    {
      question: t('How does profile verification work?'),
      answer: t(
        'You can get verified three ways: by two verified members who know you, by recording a short verification video with a challenge code from support and uploading it to an album (do not email the video file; a private album is recommended), or by meeting the admin team in person at events.'
      ),
    },
    {
      question: t('What does verification actually mean?'),
      answer: t(
        'Verification helps confirm there is a real person behind a profile, but it is not a guarantee of behavior or compatibility. Always use your own judgment and boundaries.'
      ),
    },
  ]
}

function getIntroContentSections(t: (text: string) => string): IntroSplitFeatureSection[] {
  return [
    {
      eyebrow: t('Destinations'),
      heading: t('Spicy Locations'),
      body: t(
        "Find the world's most exclusive lifestyle clubs, resorts, and private meetups. Our curated map helps you discover where the heat is, anywhere on the globe."
      ),
      imageSrc: locationsPicture.img.src,
      imageAlt: t('Lifestyle swinger rooftop swimming pool at night with ambient lighting'),
      imageSide: 'left' as const,
      linkLabel: t('Explore Locations'),
      to: '/place',
      imagePicture: locationsPicture,
      imageObjectPosition: 'center 80%',
    },
    {
      eyebrow: t('Experience'),
      heading: t('Exclusive Events'),
      body: t(
        "Whether it's a private lifestyle house party, a night on the town, or a weekend under the stars at our lifestyle camping events, we curate the spaces where you can be yourself. Stay in the loop and never miss a night (or weekend) of play."
      ),
      imageSrc: eventsPicture.img.src,
      imageAlt: t('Sophisticated lifestyle evening event with lighting and stylish atmosphere'),
      imageSide: 'right' as const,
      linkLabel: t('Explore Events'),
      to: '/event',
      imagePicture: eventsPicture,
      imageObjectPosition: 'center 60%',
    },
    {
      eyebrow: t('Connection'),
      heading: t('Niche Groups'),
      body: t(
        'Find your specific tribe. Join lifestyle swinger groups based on interests, regions, or play-styles. Deep connections start with shared passions.'
      ),
      imageSrc: groupsPicture.img.src,
      imageAlt: t('Lifestyle swinger group of adults laughing together in a cocktail bar'),
      imageSide: 'left' as const,
      linkLabel: t('Explore Groups'),
      to: '/group/list',
      imagePicture: groupsPicture,
      imageObjectPosition: 'center 10%',
    },
    {
      eyebrow: t('Literature'),
      heading: t('Cliterature'),
      body: t(
        'Discover erotic books and spicy short fiction curated by members. Whether you want a quick thrill or a slow burn, Cliterature is your shelf for sensual storytelling.'
      ),
      imageSrc: cliteraturePicture.img.src,
      imageAlt: t('Open book on black silk with a rose petal and warm candlelight, sultry burgundy mood lighting'),
      imageSide: 'right' as const,
      linkLabel: t('Explore Cliterature'),
      to: '/publication/list',
      imagePicture: cliteraturePicture,
    },
    {
      eyebrow: t('Marketplace'),
      heading: t('Vendor Market'),
      body: t(
        'Discover trades, services, and specialty businesses from members and professionals who understand the lifestyle community. Browse by category and distance to find exactly what you need.'
      ),
      imageSrc: vendorMarketPicture.img.src,
      imageAlt: t('Mosaic of lifestyle marketplace categories in warm red and dark professional tones'),
      imageSide: 'left' as const,
      linkLabel: t('Explore Vendor Market'),
      to: '/vendor-market',
      imagePicture: vendorMarketPicture,
    },
  ]
}

export const Route = createFileRoute('/(public)/(main-layout)/')({
  staticData: {
    canonicalPathname: '/',
    disableSeasonalTheme: true,
  },
  component: HomePage,
  head: () => homePageHeadFromLoaderData(),
  loader: ({ context }) => {
    const session = context.queryClient.getQueryData(authQueryKeys.session) as BetterAuthSessionData | undefined
    if (session?.session) {
      throw redirect({
        to: '/feed',
      })
    }
  },
})

function HomePage() {
  return <GuestLandingPage />
}

const featureCardClass =
  'flex h-full cursor-pointer flex-col items-center gap-6 rounded-2xl border border bg-card p-8 text-center backdrop-blur-md transition-all duration-500 ease-out hover:-translate-y-2 hover:border-red-600/30 hover:bg-red-600/5'

function GuestLandingPage() {
  const { t } = useTranslation()
  const featureCards = getFeatureCards(t)
  const valueProps = getValueProps(t)
  const faqItems = getFaqItems(t)
  const introContentSections = getIntroContentSections(t)

  return (
    <div className="flex w-full flex-col gap-0">
      {/* Hero Section */}
      <div className="relative flex min-h-screen w-full items-center justify-center overflow-hidden bg-[linear-gradient(to_bottom_right,rgba(220,38,38,0.15),transparent)] bg-gray-900 pt-14">
        <div className="absolute inset-0 z-0 h-full w-full">
          <picture>
            {Object.entries(heroPictureDesktop.sources).map(([format, srcSet]) => (
              <source key={format} media="(min-width: 768px)" sizes="100vw" srcSet={srcSet} type={`image/${format}`} />
            ))}

            {Object.entries(heroPictureMobile.sources).map(([format, srcSet]) => (
              <source key={format} sizes="100vw" srcSet={srcSet} type={`image/${format}`} />
            ))}

            <img
              alt={t('People socializing at a bar')}
              className="absolute inset-0 h-full w-full object-cover opacity-60"
              decoding="async"
              fetchPriority="high"
              height={heroPictureDesktop.img.h}
              sizes="100vw"
              src={heroPictureDesktop.img.src}
              width={heroPictureDesktop.img.w}
            />
          </picture>
        </div>
        <div className="relative z-10 mx-auto flex max-w-5xl flex-col gap-6 px-6 text-center">
          <h1 className="font-black text-5xl text-white uppercase italic leading-none md:text-8xl">
            {t('Life is too short for ')}
            <span className="text-red-600 [filter:drop-shadow(0_0_5px_rgba(0,0,0,0.5))]">{t('bland')}</span>
            {t(' nights')}
          </h1>
          <div className="flex flex-col gap-2">
            <div className="rounded-xl bg-black/60 px-4 py-1">
              <p className="font-extralight text-gray-400 text-xl italic tracking-widest md:text-2xl">
                {t('Connect with open-minded couples and singles who crave more. ')}
              </p>
            </div>
          </div>
          <div className="flex flex-col items-center justify-center gap-6 md:flex-row">
            <Link className="transition-transform duration-200 hover:scale-105" to="/auth/signup">
              <span className="relative inline-flex cursor-pointer overflow-hidden rounded-xl bg-[linear-gradient(to_bottom_right,rgba(250,115,115,0.8),rgba(170,24,24,0.8))] px-10 py-4 font-bold text-lg text-white shadow-xl">
                <span
                  aria-hidden
                  className="home-cta-shimmer-sweep pointer-events-none absolute inset-y-0 left-0 w-[45%] bg-[linear-gradient(to_right,transparent,rgba(196,125,125,0.55),transparent)]"
                />
                <span className="relative z-10">{t('Start the Heat')}</span>
              </span>
            </Link>
          </div>
        </div>
      </div>

      {/* Tagline Band */}
      <div className="w-full bg-muted py-24">
        <div className="mx-auto flex max-w-7xl flex-col gap-8 px-6 text-center">
          <h2 className="font-extrabold text-4xl leading-tight tracking-tight md:text-6xl">
            {t('The premier')} <span className="text-red-600">{t('free swinger')}</span> {t('social network.')}
          </h2>
          <div className="mx-auto -mt-2 h-1 w-24 bg-red-600" />
        </div>
      </div>

      {/* Feature Grid */}
      <div className="w-full bg-muted/60 py-16 md:py-32">
        <div className="mx-auto grid max-w-7xl grid-cols-2 items-stretch gap-4 px-6 lg:grid-cols-4 lg:gap-8">
          {featureCards.map((card, index) => {
            const isLast = index === featureCards.length - 1
            const cardInner = (
              <div className={featureCardClass}>
                <div className="relative text-primary">{card.icon}</div>
                <p className="font-black text-sm uppercase tracking-[0.2em]">{card.label}</p>
              </div>
            )

            if (isLast) {
              return (
                <div className="col-span-2 flex w-full justify-center lg:col-span-4" key={card.label}>
                  <div className="w-full">
                    <Link className="block h-full text-inherit no-underline" to={card.to}>
                      {cardInner}
                    </Link>
                  </div>
                </div>
              )
            }

            return (
              <Link className="block h-full text-inherit no-underline" key={card.label} to={card.to}>
                {cardInner}
              </Link>
            )
          })}
        </div>
      </div>

      {/* Content Sections */}
      <div className="w-full py-20">
        <div className="flex flex-col gap-20 md:gap-32">
          {introContentSections.map((section) => (
            <SplitFeatureSection
              body={section.body}
              eyebrow={section.eyebrow}
              footerLabel={section.linkLabel}
              footerTo={section.to}
              heading={section.heading}
              imageAlt={section.imageAlt}
              imageObjectPosition={section.imageObjectPosition}
              imagePicture={section.imagePicture}
              imageProps={section.imageProps}
              imageSide={section.imageSide}
              imageSrc={section.imageSrc}
              key={section.heading}
            />
          ))}
        </div>
      </div>

      {/* Mission Section */}
      <div className="relative w-full overflow-hidden py-24 md:py-40">
        <div className="absolute inset-0 z-0 h-full w-full">
          <picture>
            <source sizes="100vw" srcSet={missionPicture.sources.avif} type="image/avif" />
            <source sizes="100vw" srcSet={missionPicture.sources.webp} type="image/webp" />
            <img
              alt={t('Moody cinematic luxury dark interior')}
              className="absolute h-full w-full object-cover"
              decoding="async"
              height={missionPicture.img.h}
              src={missionPicture.img.src}
              width={missionPicture.img.w}
            />
          </picture>
          <div className="absolute inset-0 h-full w-full bg-black/80 backdrop-blur-sm" />
        </div>
        <div className="relative z-10 mx-auto flex max-w-4xl flex-col gap-12 px-6 text-center">
          <p
            aria-hidden
            className="pointer-events-none absolute -top-10 left-1/2 -translate-x-1/2 select-none font-black text-6xl text-white/10 uppercase leading-none md:text-8xl"
          >
            {t('Our Mission')}
          </p>
          <p className="font-light text-2xl text-gray-200 leading-snug md:text-3xl">
            {t(
              'We believe that exploration and connection should be built on a foundation of trust, freedom, and high-energy excitement.'
            )}
          </p>
          <p className="font-light text-2xl text-gray-200 leading-snug md:text-3xl">
            {t(
              'Spicy Friends was founded to provide a sophisticated digital sanctuary for the swinger and polyamorous community to thrive without boundaries.'
            )}
          </p>
          <p className="font-light text-2xl text-gray-200 leading-snug md:text-3xl">
            {t(
              'Our platform is designed for those who demand more from their social life — more spice, more security, and more authenticity.'
            )}
          </p>
        </div>
      </div>

      {/* Value Props */}
      <div className="w-full bg-muted py-16 md:py-32">
        <ValueProps items={valueProps} />
      </div>

      {/* FAQ Section */}
      <div className="w-full bg-muted/60 py-16 md:py-32">
        <div className="mx-auto flex max-w-4xl flex-col gap-16 px-6">
          <FaqList items={faqItems} />
        </div>
      </div>

      {/* Final CTA */}
      <div className="flex w-full bg-gradient-to-b from-muted/60 to-transparent pb-20 md:pb-28">
        <div className="mx-auto max-w-4xl px-6">
          <BringTheHeat ctaTo="/auth/signup" />
        </div>
      </div>
    </div>
  )
}
